<script lang="ts" setup>
import { storeToRefs } from "pinia";
import { useStoreDeck } from "~/stores/storeDeck";
import { NotifType, useStoreApp } from "~/stores/storeApp";
import { computed, ref } from "vue";
import { BigNumber, ethers } from "ethers";
import useConvert from "~/composables/useConvert";
import { useStoreUserV2 } from "~/stores/storeUserV2";
import { displayError } from "~/utils/errors";
import { useI18n } from "vue-i18n";
import { CardType, Currency, NftEquipment, NftSkill as TNftSkill } from "fungi-types";
import Button from "../formsV2/Button.vue";
import NftSkill from "~/components/marketplace/NftSkill.vue";
import { showNotification, ToastType } from "~/shadcn/components/ui/sonner/custom-toast";
import { OrderWithCounter } from "@opensea/seaport-js/lib/types";
import { auctionApi } from "~/api/auction";
import { formatDate } from "~/utils/dates";
import { encodeFunctionData, erc721Abi, getAddress, parseUnits } from "viem";

import { useWeb3AuthStore } from "~/stores/storeWeb3auth";
const web3AuthStore = useWeb3AuthStore();

const { maticToWei } = useConvert;
const storeDeck = useStoreDeck();
const storeUser = useStoreUserV2();
const wpolBidAmount = ref(maticToWei(0));
const wpolMinAmount = ref(maticToWei(1));
const usdcMinAmount = ref(1);
const usdcAmount = ref(0);
const decimals = ref(3);
const { selected } = storeToRefs(storeDeck);
const loading = ref(false);
const { t } = useI18n();

const emits = defineEmits<{
  (event: "closed"): void;
}>();

const storeApp = useStoreApp();

const amountTooLow = computed(() => {
  if (selectedCurrency.value === Currency.USDC) {
    return usdcAmount.value < usdcMinAmount.value;
  } else {
    return wpolBidAmount.value.lt(wpolMinAmount.value);
  }
});

const updateWpolBidAmount = (e: BigNumber) => {
  wpolBidAmount.value = e;
};

const selectedCurrency = computed(() => {
  // return storeUser.migrationToUSDCCompleted ? Currency.USDC : Currency.WMATIC;
  return Currency.USDC;
});

async function list() {
  try {
    loading.value = true;
    if (!web3AuthStore.seaportService) return;

    // 1. Get listing params from backend
    const params = await auctionApi.orders.secondary.listingParams.query({
      tokenId: selected.value.tokenId,
      amount: parseUnits(usdcAmount.value.toString(), 6).toString(),
      cardType: selected.value.cardType,
      currency: selectedCurrency.value === Currency.USDC ? Currency.USDC : undefined,
    });

    let orderWithCounter: OrderWithCounter | null = null;
    if (!selected.value.owner) {
      displayError(t("modals.list.messages.error-no-owner"));
      loading.value = false;
      return;
    }

    if (
      web3AuthStore.seaportSmartWalletService &&
      web3AuthStore.smartWalletAddress &&
      selected.value.owner.toLowerCase() === web3AuthStore.smartWalletAddress.toLowerCase()
    ) {
      console.log("Listing via Smart Wallet");
      orderWithCounter = await web3AuthStore.seaportSmartWalletService.getOrderSignature(
        params,
        storeUser.smartWalletAddress
      );
    } else if (selected.value.owner.toLowerCase() === web3AuthStore.walletAddress.toLowerCase()) {
      console.log("Listing via EOA Wallet");
      orderWithCounter = await web3AuthStore.seaportService.getOrderSignature(
        params,
        storeUser.walletAddress
      );
    } else {
      displayError(t("modals.list.messages.error-no-owner"));
      loading.value = false;
      return;
    }

    const { success, nft } = await auctionApi.auctions.secondary.create.mutate({
      tokenId: selected.value.tokenId,
      signature: orderWithCounter.signature,
      cardType: selected.value.cardType,
      parameters: orderWithCounter.parameters,
    });

    if (success && nft) {
      storeApp.displayNotification(NotifType.LIST_SUCCESS, nft);
      storeDeck.updateItem(selected.value.tokenId, nft);
      emits("closed");
    } else {
      storeApp.displayNotification(
        NotifType.LIST_FAILED,
        selected.value.auction ??
          (selected.value.cardType === CardType.NftEquipment
            ? {
                firstName: (selected.value as NftEquipment).title,
                lastName: "",
                skill: "",
                scarcity: "",
              }
            : {
                firstName: (selected.value as TNftSkill).firstName,
                lastName: (selected.value as TNftSkill).lastName,
                skill: (selected.value as TNftSkill).skill,
                scarcity: (selected.value as TNftSkill).scarcity,
              })
      );
      displayError(t("modals.list.messages.error-unlist"));
    }
    loading.value = false;
  } catch (e: any) {
    console.error("Error listing NFT:", e);
    if (e.response?.data?.code === "E_NFT_IN_GW") {
      displayError(t("modals.list.messages.error-in-gw"));
    } else if (e.response?.data?.code === "E_NFT_IN_ACADEMY") {
      displayError(t("modals.list.messages.error-in-academy"));
    } else if (e?.code === "INSUFFICIENT_FUNDS") {
      showNotification({
        title: t("messages.list-failed"),
        message: t("pages.marketplace.trade.errors.insufficient-matic-balance"),
        type: ToastType.ERROR,
        duration: 5_000,
      });
    } else {
      displayError(t("modals.list.messages.error"));
    }
    loading.value = false;
  }
}

const saleIsLocked = computed(() => {
  if (selected.value.saleUnlocksAt) {
    const now = new Date();
    const unlockDate = new Date(selected.value.saleUnlocksAt);
    return now < unlockDate;
  }
  return false;
});
</script>

<template>
  <div class="grid grid-cols-3 text-white md:max-w-[800px]">
    <div class="hidden md:flex mx-auto bg-dark-blury gradient-border bg-left">
      <div class="flex flex-col">
        <NftSkill :nft="selected" :background="false">
          <template #footer-data>
            <CardDataFooterSkeleton>
              <CardDataFooterScore
                :l10="selected.score"
                :skill="selected.skill"
                :scarcity="selected.scarcity"
                :token-id="selected.tokenId"
                :l5="selected.l5"
                :season="selected.season"
                :court="selected.courtType"
              />
            </CardDataFooterSkeleton>
          </template>
        </NftSkill>
        <span class="text-center font-display uppercase title-xs-baseline"
          >{{ selected.firstName }} {{ selected.lastName }}</span
        >
      </div>
    </div>
    <div
      class="flex flex-col justify-around gap-2 md:gap-f3 col-span-3 md:col-span-2 bg-dark-blury before:z-[-1] shadow-xl py-8 px-f4 rounded-2xl md:rounded-l-none md:before:rounded-r-2xl md:gradient-border"
    >
      <div class="flex items-center justify-between">
        <h1 class="title-sm d-s:title-md font-display uppercase">{{ $t("modals.list.title") }}</h1>
        <button>
          <icon-mingcute:close-fill @click="emits('closed')" class="w-f4 h-f4" />
        </button>
      </div>
      <div class="flex flex-col gap-f1 px-f3 md:hidden items-center justify-center">
        <div class="flex flex-col w-4/6">
          <NftSkill :nft="selected" :background="false" />
          <span class="text-center font-display uppercase title-xs-baseline"
            >{{ selected.firstName }} {{ selected.lastName }}</span
          >
        </div>
      </div>
      <div v-if="saleIsLocked" class="flex flex-col gap-f3 bg-black/50 p-4 rounded-lg py-8 text-sm">
        <p>
          <span class="font-bold uppercase"> {{ t("_sale-locked.title") }} </span><br />
          {{ t("_sale-locked.description") }}
        </p>
        <p>
          {{ t("_sale-locked.unlock-date") }}
          <span class="underline font-normal">
            {{ formatDate(selected.saleUnlocksAt as string, "dd MMMM 'à' hh'h'mm") }}
          </span>
        </p>
      </div>
      <div v-else class="flex flex-col gap-3">
        <span class="font-bold uppercase">{{ $t("modals.list.total") }}</span>

        <div
          v-if="selectedCurrency === Currency.USDC"
          class="flex gap-2 bg-white/10 rounded-lg p-3 items-center text-lg"
        >
          $
          <input
            type="number"
            class="input-primary w-full bg-transparent text-white placeholder-white/50 focus:ring-0 outline-none text-lg"
            :min="0.5"
            :increment="0.25"
            :disabled="loading"
            placeholder="0.000"
            v-model="usdcAmount"
          />
        </div>
        <ConverterInput
          v-else
          @wei-amount="updateWpolBidAmount"
          :hideConversion="false"
          :decimals="decimals"
          :canSwitchMode="false"
        >
          <template #msg v-if="amountTooLow">
            <span class="text-error">{{ $t("modals.bid.toolow") }}</span>
          </template>
        </ConverterInput>

        <span v-if="amountTooLow" class="text-error">{{ $t("modals.bid.toolow") }}</span>
      </div>

      <Button :disabled="amountTooLow" type="primary-blue" class="uppercase" @click="list">
        <icon-fgc-loading v-if="loading" />
        <span v-else>{{ $t("modals.list.confirm") }}</span>
      </Button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.bg-left {
  @apply py-f3 px-f1 rounded-l-2xl before:rounded-l-2xl before:z-[-1] shadow-xl;
}
</style>
